import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, MessageCircleQuestion, Flame, HelpCircle, MessageCircle, Mail, Globe, Users, Siren, ShieldAlert, HeartCrack, UserRound, HandHeart, Lightbulb, MoreHorizontal, TrendingUp } from "lucide-react";
import { apiFetch, apiPost } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles } from "./roleSimulator";

// Relatório de curadoria do agente Barnabé -- ADMIN-only (pedido do Rodrigo:
// "para que seja visualizado só por quem é admin"). Mesmo padrão de gate das
// outras telas /admin/* (ver AdminAcolhimento.jsx): checa /auth/me e só
// renderiza o conteúdo se a pessoa tiver o papel ADMIN.
const ADMIN_PATH = "/admin";

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)",
  marginBottom: 16
};

const sectionTitleStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  margin: "0 0 12px",
  fontSize: 14,
  fontWeight: 800,
  color: "#F8FAFC",
  textTransform: "uppercase",
  letterSpacing: 0.4
};

// Linha fixa (nao so' no estado vazio) explicando de onde cada card vem --
// 4 cards pareciam a mesma coisa (todos "alerta"/"urgente"), mas sao 2
// fontes bem diferentes: alertas estruturados do Barnabe (banco, tempo
// real) vs. leitura da IA sobre o texto das conversas, depois do fato
// (pedido do Rodrigo, achando os 4 confusos entre si).
const sectionHintStyle = { margin: "-8px 0 12px", fontSize: 12, color: "#9fb0cc", lineHeight: 1.5 };

// Barra fixa no topo com o total de pendências (itens sem assumir + temas
// urgentes e perguntas sem resposta COM contato, ainda não resolvidos) --
// antes só dava pra saber "quanto falta fazer" rolando a tela inteira até o
// fim; ela fica grudada no topo (position: sticky) com atalho de clique pra
// pular direto pra cada bloco (achado da revisão de UX: ação só existia em
// 1 de 8 cards, sem nenhum jeito de navegar entre eles numa tela comprida).
const stickyBarStyle = {
  position: "sticky",
  top: 0,
  zIndex: 20,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 10,
  background: "rgba(10,16,28,0.94)",
  backdropFilter: "blur(6px)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 12,
  padding: "10px 14px",
  marginBottom: 16,
  boxShadow: "0 8px 20px rgba(0,0,0,0.35)"
};

const jumpButtonStyle = {
  border: "1px solid rgba(226,232,240,0.2)",
  background: "rgba(226,232,240,0.06)",
  color: "#e5ebf7",
  borderRadius: 999,
  padding: "5px 12px",
  fontSize: 11.5,
  fontWeight: 800,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap"
};

// Separador entre o grupo de "pendências" (precisa de alguém agora) e o
// grupo "visão geral" (estatística/leitura, sem ação) -- antes os 8 cards
// ficavam todos com o mesmo peso visual, competindo por atenção com o que
// de fato precisa de uma pessoa (outro achado da revisão de UX).
const groupDividerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  margin: "22px 0 14px"
};

const inputStyle = {
  border: "1px solid rgba(226,232,240,0.16)",
  background: "rgba(12,20,34,0.68)",
  color: "#F8FAFC",
  borderRadius: 10,
  padding: "9px 11px",
  fontSize: 13,
  fontFamily: "inherit",
  boxSizing: "border-box",
  outline: "none",
  // Sem isso o calendario nativo do input[type=date] renderiza no tema claro
  // padrao do navegador (fundo branco, texto claro em cima de branco em
  // alguns navegadores/SOs) -- ilegivel numa tela toda escura. Mesma
  // convencao ja usada em todo input de data do app (AdminMembros.jsx,
  // AdminCriancas.jsx, Eventos.jsx, PrototypeApp.js).
  colorScheme: "dark"
};

const buttonStyle = {
  border: "1px solid rgba(230,177,90,0.45)",
  background: "rgba(230,177,90,0.14)",
  color: UI.brand,
  borderRadius: 10,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer",
  fontFamily: "inherit"
};

// Atalhos de período (pedido do Rodrigo: toda vez que ia conferir o
// relatório tinha que escolher as duas datas na mão no calendário --
// comum demais pra isso ("hoje", "essa semana") pra não ter atalho). Cada
// atalho preenche De/Até e já gera o relatório na hora, sem precisar
// clicar duas vezes.
const DATE_RANGE_PRESETS = [
  { id: "hoje", label: "Hoje" },
  { id: "ontem", label: "Ontem" },
  { id: "3dias", label: "Últimos 3 dias" },
  { id: "7dias", label: "Últimos 7 dias" },
  { id: "30dias", label: "Últimos 30 dias" },
  { id: "mes", label: "Este mês" }
];

const presetButtonStyle = (active) => ({
  border: active ? "1.5px solid rgba(230,177,90,0.9)" : "1px solid rgba(226,232,240,0.18)",
  background: active ? "rgba(230,177,90,0.28)" : "rgba(12,20,34,0.5)",
  color: active ? UI.brand : "#c7ced9",
  borderRadius: 999,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: active ? 800 : 700,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap"
});

const statStyle = { textAlign: "center" };
const statNumberStyle = { margin: 0, fontSize: 28, fontWeight: 800, color: UI.brand };
const statLabelStyle = { margin: "2px 0 0", fontSize: 12, color: "#9fb0cc" };

const SEVERITY_LABEL = { urgent: "Urgente", high: "Importante", normal: "Cuidado" };
// Reservado "urgent" como a UNICA severidade com fundo tingido -- antes
// "high" (Importante) tinha quase o mesmo peso visual (fundo + borda
// saturada), e como ele é a severidade mais comum da lista, 4-6 itens
// "Importante" empilhados viravam uma parede amarela contínua que competia
// com o vermelho de verdade (achado do Rodrigo, 24/10/2026, 3a revisão de
// UX: "tem alerta pra td lado"). "high"/"normal" agora só têm borda fina +
// texto legível, sem fundo -- o olho vai direto pro vermelho.
const SEVERITY_COLOR = {
  urgent: { color: "#FCA5A5", border: "rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.12)" },
  high: { color: "#FDE68A", border: "rgba(250,204,21,0.38)", background: "transparent" },
  normal: { color: "#93C5FD", border: "rgba(96,165,250,0.3)", background: "transparent" }
};

// Faixa de destaque do CARD por gravidade (borda esquerda + padding/raio)
// -- so "urgent" ganha fundo tingido (é o único que precisa parar o olho
// na hora); "high" vira uma borda fina de 2px sem fundo, "normal" sem
// nenhum destaque. Antes "high" tinha fundo+borda quase do mesmo peso do
// "urgent", o que é o que causava a "parede amarela" acima.
const SEVERITY_CARD_STYLE = {
  // Fundo tingido removido (25/10/2026, achado do Rodrigo: "tudo vermelho") --
  // com a barra + o badge "URGENTE" + o botao "Assumir agora" ja solidos em
  // vermelho, o fundo era uma 4a camada da mesma cor no mesmo card, sem
  // acrescentar sinal novo. A barra de 4px sozinha ja continua marcando o
  // card inteiro a distancia.
  urgent: { borderLeft: "4px solid #EF4444", background: "transparent", borderRadius: 10, padding: "10px 10px 12px" },
  high: { borderLeft: "2px solid rgba(250,204,21,0.4)", background: "transparent", borderRadius: 0, padding: "0 0 10px" },
  normal: { borderLeft: "2px solid transparent", background: "transparent", borderRadius: 0, padding: "0 0 10px" }
};

// Pra ordenar/escolher a gravidade mais alta de um grupo (a barra do grupo usa a mais alta
// entre os itens dele -- ver SEVERITY_RANK abaixo).
const SEVERITY_RANK = { urgent: 3, high: 2, normal: 1 };

// Icone fixo por categoria -- cria um "fingerprint" reconhecivel por forma
// antes mesmo de ler o texto, pra nao depender so' da pilula do badge pra
// diferenciar itens empilhados que, em texto corrido, parecem parecidos.
const CATEGORY_ICON = {
  crise_vida: Siren,
  abuso_violencia: ShieldAlert,
  luto_doenca: HeartCrack,
  pedido_pastor: UserRound,
  duvida_grave: HelpCircle,
  pedido_oracao: HandHeart,
  conexao_jovens: Users,
  duvida_pendente: MessageCircleQuestion,
  sugestao_tema: Lightbulb,
  outro: MoreHorizontal
};

// Rótulo humano pra categoria -- antes mostrava o slug técnico cru
// (pedido_pastor, crise_vida) direto na tela, junto de badges já
// traduzidos (Urgente/Importante/Cuidado); lista vem do enum de
// categorias do agente (ver agent/index.js, tool escalate_to_pastor).
const CATEGORY_LABEL = {
  crise_vida: "Crise de vida",
  abuso_violencia: "Abuso / violência",
  luto_doenca: "Luto / doença grave",
  pedido_pastor: "Pedido pra falar com o pastor",
  duvida_grave: "Dúvida grave",
  pedido_oracao: "Pedido de oração",
  conexao_jovens: "Conexão com jovens",
  duvida_pendente: "Dúvida sem resposta",
  sugestao_tema: "Sugestão de tema",
  outro: "Outro"
};

// Formata a data (ano-mes-dia) sempre no fuso de Sao Paulo, nao no fuso de
// quem esta usando o admin nem em UTC -- a igreja e' em Osasco/SP, entao
// "hoje"/"ontem" tem que bater com o dia em Sao Paulo. Antes usava
// toISOString() (UTC): durante 21h-23h59 em Sao Paulo o relogio UTC ja
// virou o dia seguinte, entao clicar "Hoje" pegava o dia de amanha e
// "Ontem" pegava hoje -- bug relatado pelo Rodrigo (filtro "Hoje" sem
// conversa nenhuma, mesmo com movimento no dia).
const RELATORIO_TZ = "America/Sao_Paulo";
function fmtDateInput(d) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: RELATORIO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(d);
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day}`;
}

// Data + hora (fuso de São Paulo) pra exibir quando cada alerta foi
// sinalizado -- antes a tela não mostrava isso em lugar nenhum, então não
// dava pra saber se um item "sem assumir" era de agora ou de dois dias
// atrás só olhando o card.
function fmtDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: RELATORIO_TZ,
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(d);
}

// "1 conversa" / "2 conversas" -- antes o texto vinha fixo no plural
// ("1 conversas"), erro de concordância achado na revisão de UX.
function pluralConversas(n) {
  return `${n} conversa${n === 1 ? "" : "s"}`;
}

// Divide o resumo (string única vinda do backend, formato "Necessidade
// percebida: ... Trecho: ...") em leitura da IA + citação literal da
// pessoa -- são naturezas de informação diferentes (interpretação vs.
// fato) e emendadas no mesmo parágrafo dificultavam saber o que era
// citação direta (achado da revisão de UX de 23/10/2026).
function splitResumo(resumo) {
  if (!resumo) return { leitura: resumo, trecho: null };
  const idx = resumo.indexOf("Trecho:");
  if (idx === -1) return { leitura: resumo, trecho: null };
  return {
    leitura: resumo.slice(0, idx).trim(),
    trecho: resumo.slice(idx + "Trecho:".length).trim()
  };
}

// Agrupa "itens sinalizados sem assumir" pela MESMA pessoa (personId, a
// mesma chave de identidade usada no backend -- ver resolvedContato em
// agentReport.js) em vez de listar cada alerta solto. Sem isso, a mesma
// pessoa com vários alertas abertos (ex.: a Josi com 3) aparecia espalhada
// na lista, intercalada com gente diferente, e duas pessoas da equipe
// podiam assumir pedaços da mesma situação sem perceber que é a mesma
// pessoa (pedido do Rodrigo, 23/10/2026, vendo o print do relatório).
// Preserva a ordem (por gravidade/data) que já vem do backend: a posição
// do grupo é a posição do primeiro item dessa pessoa na lista já ordenada.
function groupAlertasByPerson(items) {
  const order = [];
  const byKey = new Map();
  for (const a of items || []) {
    const key = a.personId || `solo_${a.id}`;
    if (!byKey.has(key)) {
      byKey.set(key, []);
      order.push(key);
    }
    byKey.get(key).push(a);
  }
  return order.map((key) => ({ key, itens: byKey.get(key) }));
}

// Janela curta pra considerar 2+ sinalizacoes da MESMA pessoa+categoria uma
// CADEIA DE ESCALONAMENTO (o Barnabe reavisando porque a gravidade subiu no
// meio da conversa -- ver guards.js: alertGate deixa passar de novo so'
// quando a gravidade aumenta, de proposito, pra nunca engolir uma piora) em
// vez de 2 ocorrencias de verdade separadas (ex.: o mesmo assunto voltando a
// tona dias depois). 15min cobre bem uma troca de mensagens numa conversa so'
// sem juntar coisas que nao tem nada a ver (achado do Rodrigo, 24/10/2026,
// 2a revisao de UX desta tela: metade da lista da Josi era o mesmo relato
// contado 2x com peso visual igual).
const ESCALATION_WINDOW_MIN = 15;

// Agrupa os itens de UMA pessoa (ja' vindos de groupAlertasByPerson) em
// unidades de renderizacao: "single" (item isolado, nada pra juntar) ou
// "chain" (2+ sinalizacoes da mesma categoria dentro da janela acima,
// cronologicas). Preserva a posicao do item mais grave na ordem original
// (ja' vem ordenada por gravidade do backend) -- a cadeia aparece onde a
// entrada mais grave dela apareceria sozinha.
function buildRenderUnits(itens) {
  const porCategoria = new Map();
  for (const a of itens) {
    const arr = porCategoria.get(a.categoria) || [];
    arr.push(a);
    porCategoria.set(a.categoria, arr);
  }
  for (const arr of porCategoria.values()) arr.sort((x, y) => new Date(x.criadoEm) - new Date(y.criadoEm));

  const emitidos = new Set();
  const units = [];
  for (const a of itens) {
    if (emitidos.has(a.id)) continue;
    const mesmoAssunto = porCategoria.get(a.categoria);
    if (mesmoAssunto.length === 1) {
      units.push({ kind: "single", item: a });
      emitidos.add(a.id);
      continue;
    }
    const inicio = new Date(mesmoAssunto[0].criadoEm).getTime();
    const fim = new Date(mesmoAssunto[mesmoAssunto.length - 1].criadoEm).getTime();
    if (fim - inicio <= ESCALATION_WINDOW_MIN * 60_000) {
      units.push({ kind: "chain", items: mesmoAssunto });
      mesmoAssunto.forEach((x) => emitidos.add(x.id));
    } else {
      units.push({ kind: "single", item: a });
      emitidos.add(a.id);
    }
  }
  return units;
}

// Linha compacta pra uma sinalizacao ANTERIOR dentro de uma cadeia de
// escalonamento -- so' badge + hora + leitura truncada numa linha, sem
// repetir trecho/contato (que ja' aparecem na entrada final, mais grave,
// logo abaixo). Objetivo e' nao perder a informacao (continua tudo visivel)
// sem dar o mesmo peso visual de um card inteiro pra algo que ja' foi
// superado por uma gravidade maior.
function EscalationPriorLine({ a }) {
  const { leitura } = splitResumo(a.resumo);
  const quando = fmtDateTime(a.criadoEm);
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "baseline", padding: "2px 0 4px 2px", opacity: 0.6 }}>
      {/* Texto puro, sem pilula de badge -- a linha "Mesmo assunto escalou:
          X -> Y" (acima, no AlertUnitCard) ja' nomeia a gravidade anterior;
          repetir como badge colorido dava o mesmo peso visual de um alerta
          de verdade pra algo que ja' foi superado (pedido do Rodrigo,
          25/10/2026: "onde tem urgente n precisa ter importante"). */}
      <span style={{ fontSize: 11, color: "#7c8db0", flexShrink: 0, fontWeight: 600 }}>{SEVERITY_LABEL[a.gravidade] || a.gravidade}:</span>
      <span style={{ fontSize: 11, color: "#7c8db0", flexShrink: 0 }}>{quando}</span>
      <span style={{ fontSize: 12, color: "#9fb0cc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: "1 1 auto" }}>{leitura}</span>
    </div>
  );
}

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

// Badge de gravidade -- "Urgente" (risco de vida) ganha um tratamento
// categoricamente maior (nao so' a cor), senao ele compete em pe' de
// igualdade com um "Cuidado" qualquer na lista (achado da revisao de UX:
// o unico jeito de diferenciar gravidade era a cor, sutil demais pra uma
// tela de triagem de seguranca).
function Badge({ children, tone }) {
  const s = SEVERITY_COLOR[tone] || SEVERITY_COLOR.normal;
  const urgent = tone === "urgent";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: urgent ? 13 : 11,
        fontWeight: 800,
        padding: urgent ? "5px 12px" : "3px 9px",
        borderRadius: 999,
        color: s.color,
        border: `1px solid ${s.border}`,
        background: urgent ? "rgba(239,68,68,0.22)" : s.background,
        textTransform: urgent ? "uppercase" : "none",
        letterSpacing: urgent ? 0.3 : 0
      }}
    >
      {/* So' "urgent" leva icone no badge -- o AlertCircle de "high" somava
          mais um icone junto do icone de categoria logo ao lado (2 icones +
          pilula + texto na mesma linha), ruido que nao ajudava a diferenciar
          nada (mesmo achado do SEVERITY_COLOR acima). */}
      {urgent ? <AlertTriangle size={13} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

// Contato de quem disparou um item de "temas urgentes"/"perguntas sem
// resposta" -- antes esses dois blocos so' mostravam um trecho anonimo da
// conversa, sem jeito nenhum de achar ou falar com a pessoa (Rodrigo viu 2
// casos sinalizados e perguntou "onde vejo o contato?"). Agora a API manda
// personId + contato (nome/whatsapp/email, quando a pessoa deixou) junto de
// cada item; aqui so' renderiza o que tiver.
// Rotulo de qual canal (site ou WhatsApp) gerou a conversa -- antes o
// relatorio nao distinguia (so' misturava tudo), pedido do Rodrigo depois
// de perguntar se a curadoria enxergava as duas fontes juntas.
function CanalTag({ canal }) {
  if (!canal) return null;
  const isWhatsapp = canal === "whatsapp";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 10.5,
        fontWeight: 800,
        padding: "2px 8px",
        borderRadius: 999,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        color: isWhatsapp ? "#86EFAC" : "#7DD3FC",
        border: `1px solid ${isWhatsapp ? "rgba(134,239,172,0.35)" : "rgba(125,211,252,0.35)"}`,
        background: isWhatsapp ? "rgba(134,239,172,0.10)" : "rgba(125,211,252,0.10)"
      }}
    >
      {isWhatsapp ? <MessageCircle size={11} aria-hidden="true" /> : <Globe size={11} aria-hidden="true" />}
      {isWhatsapp ? "WhatsApp" : "Site"}
    </span>
  );
}

function ContatoLine({ contato, urgent, compact = false }) {
  if (!contato) {
    // Pra um alerta urgente (risco de vida), "sem contato" nao e' um
    // detalhe neutro -- e' a informacao que decide se da' ou nao pra agir
    // direto. Itálico cinza discreto (mesmo peso de qualquer "sem contato"
    // comum) escondia isso; aqui ganha cor/peso de atencao quando urgente.
    // compact (cabeçalho de grupo, nome já aparece do lado): some sem
    // contato vira só um traço discreto, não mais uma frase inteira --
    // repetida 1x por grupo já basta, a frase completa continua no item.
    if (compact) return urgent ? <span style={{ fontSize: 11.5, fontWeight: 700, color: "#FCA5A5" }}>sem contato salvo</span> : null;
    return urgent ? (
      <p style={{ margin: "6px 0 0", fontSize: 12, fontWeight: 800, color: "#FCA5A5", display: "flex", alignItems: "center", gap: 5 }}>
        <AlertTriangle size={13} aria-hidden="true" /> Sem contato salvo -- não há como responder direto.
      </p>
    ) : (
      <p style={{ margin: "6px 0 0", fontSize: 11.5, color: "#7c8db0", fontStyle: "italic" }}>
        Sem contato salvo pra essa conversa.
      </p>
    );
  }
  const waDigits = contato.whatsapp ? contato.whatsapp.replace(/\D/g, "") : null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: compact ? 8 : 10, margin: compact ? 0 : "6px 0 0" }}>
      {/* Nome/badge de Membro só aparecem no modo não-compacto -- no cabeçalho
          de grupo (compact) o nome já está logo ao lado, repetir aqui é o
          mesmo "Josi · Josi" que motivou esse ajuste (Rodrigo, 25/10/2026). */}
      {!compact && contato.nome ? (
        <span style={{ fontSize: 12, fontWeight: 800, color: "#F8FAFC" }}>{contato.nome}</span>
      ) : !compact ? (
        // So' temos o telefone de quem escreveu (veio do WhatsApp, nunca virou
        // cadastro no Conecta) -- melhor deixar isso explicito do que mostrar
        // um nome em branco.
        <span style={{ fontSize: 11.5, color: "#7c8db0", fontStyle: "italic" }}>Nome não identificado</span>
      ) : null}
      {!compact && contato.membro ? (
        // Identificado como Membro (logado no site, ou e-mail/telefone batendo com o cadastro --
        // ver resolvedContato em routes/agentReport.js). Borda sem preenchimento: so' informa,
        // nao compete com os badges de gravidade (que sao o unico lugar com cor solida).
        <span
          title="Essa pessoa já é Membro cadastrado"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 10.5,
            fontWeight: 700,
            color: "#86EFAC",
            border: "1px solid rgba(134,239,172,0.4)",
            borderRadius: 999,
            padding: "1px 7px",
            textTransform: "uppercase",
            letterSpacing: 0.3
          }}
        >
          <UserRound size={11} aria-hidden="true" /> Membro
        </span>
      ) : null}
      {waDigits ? (
        <a
          href={`https://wa.me/${waDigits}`}
          target="_blank"
          rel="noreferrer"
          title={compact ? "WhatsApp" : undefined}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "#86EFAC", textDecoration: "none" }}
        >
          <MessageCircle size={13} aria-hidden="true" /> {compact ? null : "WhatsApp"}
        </a>
      ) : null}
      {contato.email ? (
        <a
          href={`mailto:${contato.email}`}
          title={compact ? contato.email : undefined}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "#7DD3FC", textDecoration: "none" }}
        >
          <Mail size={13} aria-hidden="true" /> {compact ? null : contato.email}
        </a>
      ) : null}
      {!compact && !waDigits && !contato.email ? (
        <span style={{ fontSize: 11.5, color: "#7c8db0", fontStyle: "italic" }}>Sem WhatsApp/e-mail salvo.</span>
      ) : null}
    </div>
  );
}

// Card completo de um item/cadeia sinalizada -- extraído pra componente de módulo (25/10/2026,
// reformulação do card de grupo pedida pelo Rodrigo) porque agora tem DOIS jeitos de aparecer:
// sempre aberto (item urgente, ou qualquer item fora de um grupo) ou atrás de uma linha
// compacta que expande sob demanda (ver CollapsibleAlertUnit logo abaixo). Recebe como props o
// que antes vinha por closure do componente principal -- estado e handlers de assumir/drill-down
// são do relatório inteiro, não deste card.
function AlertUnitCard({ unit, multi, expandedAlertIds, contextByAlertId, onToggleContext, claimingId, claimingGroupKey, onAssumir }) {
  const isChain = unit.kind === "chain";
  const anteriores = isChain ? unit.items.slice(0, -1) : [];
  const a = isChain ? unit.items[unit.items.length - 1] : unit.item;
  const urgent = a.gravidade === "urgent";
  const quando = fmtDateTime(a.criadoEm);
  const { leitura, trecho } = splitResumo(a.resumo);
  return (
    <div
      style={{
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        // 3 faixas de destaque reais (urgent > high > normal) -- MAS so'
        // quando o item esta' sozinho (!multi). Dentro de um grupo a barra
        // colorida ja' aparece UMA vez no card da conversa (grupoGravidade,
        // ver render da secao abaixo); repetir aqui em cada item e' o
        // "exagero visual" que o Rodrigo apontou (25/10/2026: "tem uma
        // barra vermelha tem escrito importante e urgente mesmo carga").
        ...(multi ? { padding: "0 0 10px" } : (SEVERITY_CARD_STYLE[a.gravidade] || SEVERITY_CARD_STYLE.normal))
      }}
    >
      {isChain ? (
        // Cadeia de escalonamento (ver buildRenderUnits acima) --
        // mostra o caminho da gravidade antes de renderizar a entrada
        // final/mais grave abaixo, em vez de 2+ cards cheios repetindo
        // a mesma situacao.
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
          <TrendingUp size={12} color="#9fb0cc" aria-hidden="true" />
          <span style={{ fontSize: 11, color: "#9fb0cc" }}>
            Mesmo assunto escalou: {unit.items.map((x) => SEVERITY_LABEL[x.gravidade] || x.gravidade).join(" → ")} ({fmtDateTime(unit.items[0].criadoEm)} → {fmtDateTime(a.criadoEm)})
          </span>
        </div>
      ) : null}
      {anteriores.map((p) => <EscalationPriorLine key={p.id} a={p} />)}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
        <Badge tone={a.gravidade}>{SEVERITY_LABEL[a.gravidade] || a.gravidade}</Badge>
        {(() => {
          const CategoriaIcone = CATEGORY_ICON[a.categoria];
          return CategoriaIcone ? <CategoriaIcone size={13} color="#9fb0cc" aria-hidden="true" /> : null;
        })()}
        {/* Peso tipografico subiu (700->800, cor mais clara) -- categoria
            ("que tipo de ajuda chamar") é tão decisiva quanto severidade
            ("quão rápido"), mas antes ficava cinza discreto do lado de um
            badge saturado e sumia no meio do ruído de cor. */}
        <span style={{ fontSize: 12.5, fontWeight: 800, color: "#E2E8F0" }}>{CATEGORY_LABEL[a.categoria] || a.categoria}</span>
        {/* Canal/nome só repete aqui quando NÃO está agrupado -- num
            grupo (mesma pessoa) isso já está no cabeçalho acima, repetir
            em cada item é o próprio "fica confuso" que motivou agrupar. */}
        {!multi ? <CanalTag canal={a.canal} /> : null}
        {!multi && a.personName ? <span style={{ fontSize: 12, color: "#9fb0cc" }}>-- {a.personName}</span> : null}
        {quando ? <span style={{ fontSize: 11, color: "#7c8db0" }}>{quando}</span> : null}
      </div>
      <p style={{ margin: 0, fontSize: 13, color: "#e5ebf7" }}>{leitura}</p>
      {trecho ? (
        <p
          style={{
            margin: "5px 0 0",
            fontSize: 12.5,
            fontStyle: "italic",
            color: "#cbd5e1",
            borderLeft: "2px solid rgba(255,255,255,0.16)",
            paddingLeft: 8
          }}
        >
          "{trecho}"
        </p>
      ) : null}
      {/* Drill-down "o que o Barnabé respondeu" e "Encaminhar" juntos, numa linha só, como
          botões pequenos -- antes eram 2 links de texto azul empilhados (2 linhas inteiras por
          item), o que pesava muito quando vários itens aparecem juntos num grupo. */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 6, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => onToggleContext(a.id)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: "none",
            border: "none",
            padding: 0,
            color: "#7DD3FC",
            fontSize: 11.5,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit"
          }}
        >
          <span style={{ display: "inline-block", transform: expandedAlertIds.has(a.id) ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>▸</span>
          {expandedAlertIds.has(a.id) ? "Ocultar conversa" : "Ver o que o Barnabé respondeu"}
        </button>
        <ForwardButton urgent={urgent} buildContent={() => buildAlertForwardContent(a)} />
        {/* "Assumir" entra nessa mesma linha, empurrado pra direita (marginLeft:
            auto) -- mesmo lado do botao equivalente no cabecalho de grupo
            ("Assumir tudo agora"), em vez de um bloco a parte alinhado a
            esquerda como antes. */}
        {!multi ? (
          <button
            type="button"
            onClick={() => onAssumir(a.id)}
            disabled={claimingId === a.id || Boolean(claimingGroupKey)}
            style={{
              marginLeft: "auto",
              // Botao "Assumir" tambem escala com a gravidade --
              // antes era o mesmo verde discreto pra crise de
              // vida e pra sugestao de tema, mesmo peso visual
              // pra acoes de urgencia bem diferente.
              border: urgent ? "1px solid rgba(239,68,68,0.6)" : "1px solid rgba(134,239,172,0.4)",
              background: urgent
                ? claimingId === a.id
                  ? "rgba(220,38,38,0.45)"
                  : "#DC2626"
                : claimingId === a.id
                  ? "rgba(134,239,172,0.08)"
                  : "rgba(134,239,172,0.14)",
              color: urgent ? "#FFF5F5" : "#86EFAC",
              borderRadius: 8,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 800,
              cursor: claimingId === a.id ? "default" : "pointer",
              fontFamily: "inherit",
              whiteSpace: "nowrap"
            }}
          >
            {claimingId === a.id ? "Assumindo..." : urgent ? "Assumir agora" : "Assumir"}
          </button>
        ) : null}
      </div>
      {expandedAlertIds.has(a.id) ? (
        <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.08)" }}>
          {contextByAlertId[a.id]?.loading ? (
            <p style={{ margin: 0, fontSize: 12, color: "#9fb0cc" }}>Carregando...</p>
          ) : contextByAlertId[a.id]?.error ? (
            <p style={{ margin: 0, fontSize: 12, color: "#FCA5A5" }}>{contextByAlertId[a.id].error}</p>
          ) : contextByAlertId[a.id]?.mensagens?.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {contextByAlertId[a.id].mensagens.map((m, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: m.role === "assistant" ? "flex-start" : "flex-end" }}>
                  <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, color: m.role === "assistant" ? "#86EFAC" : "#7DD3FC", marginBottom: 2 }}>
                    {m.role === "assistant" ? "Barnabé" : (a.personName || a.contato?.nome || "Pessoa")}
                  </span>
                  <p
                    style={{
                      margin: 0,
                      maxWidth: "88%",
                      fontSize: 12.5,
                      lineHeight: 1.45,
                      color: "#E5EBF7",
                      background: m.role === "assistant" ? "rgba(134,239,172,0.08)" : "rgba(125,211,252,0.08)",
                      borderRadius: 8,
                      padding: "6px 9px",
                      overflowWrap: "anywhere"
                    }}
                  >
                    {m.content}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 12, color: "#9fb0cc" }}>Sem mensagens encontradas pra essa conversa.</p>
          )}
        </div>
      ) : null}
      {/* Contato só repete aqui quando NÃO está agrupado -- num
          grupo (mesma pessoa) o link de WhatsApp/e-mail já está no
          cabeçalho acima (grupoContato), uma vez só pro grupo inteiro.
          "Assumir" (item sozinho) saiu daqui -- agora fica na linha de
          acoes logo acima, ao lado de "Encaminhar" (ver comentario lá). */}
      {!multi ? <ContatoLine contato={a.contato} urgent={urgent} /> : null}
    </div>
  );
}

// Dentro de um grupo (mesma pessoa), só o(s) item(ns) URGENTE(S) ficam sempre abertos --
// Importante/Cuidado entram colapsados numa linha compacta (1 linha: gravidade, categoria,
// hora, início do resumo), clicável pra abrir o card completo. Reformulação pedida pelo
// Rodrigo (25/10/2026) depois de ver o card de grupo "muito cheio de coisa": pra uma pessoa
// com 6 itens sinalizados da mesma conversa, só o que é novo/mais grave precisa aparecer
// aberto de cara -- o resto fica a 1 clique, sem desaparecer.
function CollapsibleAlertUnit(props) {
  const [open, setOpen] = useState(false);
  const { unit } = props;
  const a = unit.kind === "chain" ? unit.items[unit.items.length - 1] : unit.item;
  const { leitura } = splitResumo(a.resumo);
  const quando = fmtDateTime(a.criadoEm);

  if (open) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "none",
            border: "none",
            padding: "0 0 4px",
            color: "#7c8db0",
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit"
          }}
        >
          <span style={{ display: "inline-block", transform: "rotate(90deg)" }}>▸</span> Recolher
        </button>
        <AlertUnitCard {...props} />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.025)",
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "left"
      }}
    >
      <Badge tone={a.gravidade}>{SEVERITY_LABEL[a.gravidade] || a.gravidade}</Badge>
      {(() => {
        const CategoriaIcone = CATEGORY_ICON[a.categoria];
        return CategoriaIcone ? <CategoriaIcone size={12} color="#9fb0cc" aria-hidden="true" /> : null;
      })()}
      <span style={{ fontSize: 11.5, fontWeight: 700, color: "#cbd5e1", flexShrink: 0 }}>{CATEGORY_LABEL[a.categoria] || a.categoria}</span>
      <span style={{ fontSize: 11, color: "#7c8db0", flexShrink: 0 }}>{quando}</span>
      <span style={{ fontSize: 12, color: "#9fb0cc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: "1 1 auto" }}>{leitura}</span>
      <span style={{ color: "#7c8db0", flexShrink: 0 }}>▸</span>
    </button>
  );
}

// Separa um bloco de insight (temas urgentes / perguntas sem resposta) em
// 3 grupos -- a distincao que o proprio Rodrigo fez entre "tem contato =
// da' pra agir" e "sem contato = so' estatistica" (23/10/2026): com contato
// e aberto (precisa de alguem), com contato mas ja' resolvido (fica
// escondido atras do "mostrar resolvidos", pra fila nao acumular lixo), e
// sem contato (nao tem o que "resolver" -- so' um dado de leitura, formato
// mais enxuto, sem botao).
function groupInsightItems(items) {
  const comContato = [];
  const semContato = [];
  for (const it of items || []) {
    (it.contato ? comContato : semContato).push(it);
  }
  return {
    abertos: comContato.filter((it) => !it.resolvido),
    resolvidos: comContato.filter((it) => it.resolvido),
    semContato
  };
}

// Botaozinho que mostra/esconde os itens com contato ja' resolvidos --
// ficam fora da fila por padrao, pra nao acumular "lixo" no que precisa de
// atencao agora (so' aparece quando ha' pelo menos 1 resolvido).
function ResolvedToggle({ n, show, onToggle }) {
  if (!n) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        marginTop: 10,
        border: "none",
        background: "none",
        color: "#7c8db0",
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "inherit",
        padding: 0
      }}
    >
      {show ? "Esconder resolvidos" : `Mostrar resolvidos (${n})`}
    </button>
  );
}

// Um item de "temas urgentes"/"perguntas sem resposta" COM contato --
// unico formato que ganha card cheio + botao de acao (resolver/reabrir).
// `children` carrega o conteudo especifico de cada bloco (porQue/trecho ou
// agente-respondeu/motivo).
// Encaminha um alerta (ou grupo) por e-mail pra alguém que não acompanha esse relatório --
// pedido do Rodrigo (25/10/2026), vendo o caso da Josi (abuso + crise de vida escalando): "tem
// como ter um botao pra encaminharmos ali na curadoria?". Lembra o último e-mail usado
// (localStorage, só neste navegador) pra não precisar redigitar toda vez.
const LAST_FORWARD_EMAIL_KEY = "agenteRelatorio.ultimoEncaminhamento";

function readLastForwardEmail() {
  try {
    return localStorage.getItem(LAST_FORWARD_EMAIL_KEY) || "";
  } catch {
    return "";
  }
}

function rememberForwardEmail(email) {
  try {
    localStorage.setItem(LAST_FORWARD_EMAIL_KEY, email);
  } catch {
    // ignora -- é só conveniência, não precisa funcionar
  }
}

function contatoParaTexto(contato) {
  if (!contato) return "Sem contato salvo pra essa conversa.";
  const linhas = [contato.nome && `Nome: ${contato.nome}`, contato.whatsapp && `WhatsApp: ${contato.whatsapp}`, contato.email && `E-mail: ${contato.email}`].filter(Boolean);
  return linhas.length ? linhas.join("\n") : "Sem contato salvo pra essa conversa.";
}

function buildAlertForwardContent(a) {
  const categoria = CATEGORY_LABEL[a.categoria] || a.categoria;
  const gravidade = SEVERITY_LABEL[a.gravidade] || a.gravidade;
  const quemE = a.personName || a.contato?.nome || "pessoa não identificada";
  const quando = fmtDateTime(a.criadoEm);
  const assunto = `[Curadoria Barnabé] ${gravidade} -- ${categoria} -- ${quemE}`;
  const corpo = [
    `Categoria: ${categoria}`,
    `Gravidade: ${gravidade}`,
    `Canal: ${a.canal === "whatsapp" ? "WhatsApp" : "Site"}`,
    quando ? `Quando: ${quando}` : null,
    "",
    a.resumo,
    "",
    "Contato:",
    contatoParaTexto(a.contato)
  ].filter((x) => x !== null).join("\n");
  return { assunto, corpo };
}

function buildGrupoForwardContent(grupo, grupoContato) {
  const quemE = grupo.itens[0]?.personName || grupoContato?.nome || "pessoa não identificada";
  const canal = grupo.itens[0]?.canal;
  const itensTexto = grupo.itens
    .slice()
    .sort((x, y) => new Date(x.criadoEm) - new Date(y.criadoEm))
    .map((a) => `- [${SEVERITY_LABEL[a.gravidade] || a.gravidade}] ${CATEGORY_LABEL[a.categoria] || a.categoria} (${fmtDateTime(a.criadoEm)}): ${a.resumo}`)
    .join("\n");
  const assunto = `[Curadoria Barnabé] ${grupo.itens.length} itens sinalizados -- ${quemE}`;
  const corpo = [
    `Pessoa: ${quemE}`,
    `Canal: ${canal === "whatsapp" ? "WhatsApp" : "Site"}`,
    `${grupo.itens.length} itens sinalizados, provavelmente a mesma conversa:`,
    "",
    itensTexto,
    "",
    "Contato:",
    contatoParaTexto(grupoContato)
  ].join("\n");
  return { assunto, corpo };
}

function ForwardButton({ buildContent, label = "Encaminhar", urgent = false, iconOnly = false }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(() => readLastForwardEmail());
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  async function handleSend() {
    const email = to.trim();
    if (!email) return;
    setSending(true);
    setResult(null);
    try {
      const { assunto, corpo } = buildContent();
      await apiPost("/admin/agent/alertas/encaminhar", { body: { to: email, assunto, corpo } });
      rememberForwardEmail(email);
      setResult({ ok: true, message: `Encaminhado pra ${email}.` });
      setOpen(false);
    } catch (error) {
      setResult({ ok: false, message: error?.message || "Não foi possível encaminhar agora." });
    } finally {
      setSending(false);
    }
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      {/* iconOnly: cabeçalho de grupo, ao lado de "Assumir tudo" -- um botão de
          texto inteiro ali competiria com a ação primária (pedido do Rodrigo,
          25/10/2026: layout "cheio de coisa"). Mantém o title pra acessibilidade. */}
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setResult(null); }}
        title={iconOnly ? label : undefined}
        style={
          iconOnly
            ? {
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 26,
                height: 26,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: 7,
                color: urgent ? "#FCA5A5" : "#9fb0cc",
                cursor: "pointer"
              }
            : {
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: "none",
                border: "none",
                padding: 0,
                color: urgent ? "#FCA5A5" : "#7DD3FC",
                fontSize: 11.5,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit"
              }
        }
      >
        <Mail size={iconOnly ? 13 : 12} aria-hidden="true" /> {iconOnly ? null : label}
      </button>
      {open ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="email@exemplo.com"
            style={{ fontSize: 11.5, padding: "3px 7px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(0,0,0,0.3)", color: "#E5EBF7", fontFamily: "inherit", minWidth: 190 }}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !to.trim()}
            style={{
              border: "1px solid rgba(125,211,252,0.4)",
              background: sending ? "rgba(125,211,252,0.08)" : "rgba(125,211,252,0.16)",
              color: "#7DD3FC",
              borderRadius: 6,
              padding: "3px 9px",
              fontSize: 11.5,
              fontWeight: 800,
              cursor: sending ? "default" : "pointer",
              fontFamily: "inherit"
            }}
          >
            {sending ? "Enviando..." : "Enviar"}
          </button>
        </span>
      ) : null}
      {result ? <span style={{ fontSize: 11, color: result.ok ? "#86EFAC" : "#FCA5A5" }}>{result.message}</span> : null}
    </span>
  );
}

// Drill-down "ver a conversa" reutilizável pra qualquer item com personId (temas urgentes e
// perguntas sem resposta) -- mesma ideia do que já existe pros itens sinalizados
// (toggleAlertContext, mais abaixo), mas auto-contido: cada card busca a própria conversa sob
// demanda, sem precisar de dicionário no componente pai. Usa /admin/agent/conversa/:personId
// (a conversa inteira, ver routes/agentReport.js) em vez de /alertas/:id/contexto porque esses
// itens vêm da leitura da IA sobre a transcrição, não de um AgentAlert com id/createdAt pra
// ancorar uma janela. Pedido do Rodrigo (25/10/2026): "era bom por o drill down nessas tbm".
function ConversationDrilldown({ personId, personName }) {
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState(null);

  if (!personId) return null;

  async function toggle() {
    setExpanded((v) => !v);
    if (state) return; // já carregado (ou carregando) antes
    setState({ loading: true, mensagens: [], error: "" });
    try {
      const data = await apiFetch(`/admin/agent/conversa/${encodeURIComponent(personId)}`, { method: "GET" });
      setState({ loading: false, mensagens: data?.mensagens || [], error: "" });
    } catch (error) {
      setState({ loading: false, mensagens: [], error: error?.message || "Não foi possível carregar a conversa." });
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        style={{
          marginTop: 6,
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          background: "none",
          border: "none",
          padding: 0,
          color: "#7DD3FC",
          fontSize: 11.5,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: "inherit"
        }}
      >
        <span style={{ display: "inline-block", transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>▸</span>
        {expanded ? "Ocultar conversa" : "Ver a conversa"}
      </button>
      {expanded ? (
        <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.22)", border: "1px solid rgba(255,255,255,0.08)" }}>
          {state?.loading ? (
            <p style={{ margin: 0, fontSize: 12, color: "#9fb0cc" }}>Carregando...</p>
          ) : state?.error ? (
            <p style={{ margin: 0, fontSize: 12, color: "#FCA5A5" }}>{state.error}</p>
          ) : state?.mensagens?.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {state.mensagens.map((m, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: m.role === "assistant" ? "flex-start" : "flex-end" }}>
                  <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.6, color: m.role === "assistant" ? "#86EFAC" : "#7DD3FC", marginBottom: 2 }}>
                    {m.role === "assistant" ? "Barnabé" : (personName || "Pessoa")}
                  </span>
                  <p
                    style={{
                      margin: 0,
                      maxWidth: "88%",
                      fontSize: 12.5,
                      lineHeight: 1.45,
                      color: "#E5EBF7",
                      background: m.role === "assistant" ? "rgba(134,239,172,0.08)" : "rgba(125,211,252,0.08)",
                      borderRadius: 8,
                      padding: "6px 9px",
                      overflowWrap: "anywhere"
                    }}
                  >
                    {m.content}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 12, color: "#9fb0cc" }}>Sem mensagens encontradas pra essa conversa.</p>
          )}
        </div>
      ) : null}
    </>
  );
}

function InsightItemCard({ titulo, canal, contato, personId, children, resolvido, resolvidoPor, resolvendo, onResolver, onReabrir }) {
  return (
    <div
      style={{
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        paddingBottom: 10,
        opacity: resolvido ? 0.6 : 1
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: "#F8FAFC", textDecoration: resolvido ? "line-through" : "none" }}>{titulo}</p>
        <CanalTag canal={canal} />
      </div>
      {children}
      <ContatoLine contato={contato} />
      <ConversationDrilldown personId={personId} personName={contato?.nome} />
      {resolvido ? (
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
          {resolvidoPor ? <span style={{ fontSize: 11.5, color: "#7c8db0" }}>Resolvido por {resolvidoPor}</span> : null}
          <button
            type="button"
            onClick={onReabrir}
            disabled={resolvendo}
            style={{
              border: "1px solid rgba(226,232,240,0.25)",
              background: "rgba(226,232,240,0.06)",
              color: "#c7ced9",
              borderRadius: 8,
              padding: "5px 10px",
              fontSize: 11.5,
              fontWeight: 700,
              cursor: resolvendo ? "default" : "pointer",
              fontFamily: "inherit"
            }}
          >
            {resolvendo ? "Reabrindo..." : "Reabrir"}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onResolver}
          disabled={resolvendo}
          style={{
            marginTop: 8,
            border: "1px solid rgba(134,239,172,0.4)",
            background: resolvendo ? "rgba(134,239,172,0.08)" : "rgba(134,239,172,0.14)",
            color: "#86EFAC",
            borderRadius: 8,
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: 800,
            cursor: resolvendo ? "default" : "pointer",
            fontFamily: "inherit"
          }}
        >
          {resolvendo ? "Marcando..." : "Marcar como resolvido"}
        </button>
      )}
    </div>
  );
}

// Itens SEM contato -- nao tem o que "resolver" (nao da' pra agir, so' e'
// leitura/estatistica), entao formato enxuto, sem card proprio nem botao,
// so' pra nao sumir da tela mas tambem nao competir visualmente com o que
// precisa de alguem.
function SemContatoList({ items, titleField, subtitleField, subtitleLabel }) {
  if (!items?.length) return null;
  return (
    <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px dashed rgba(255,255,255,0.1)" }}>
      <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 800, color: "#7c8db0", textTransform: "uppercase", letterSpacing: 0.4 }}>
        Sem contato -- só estatística ({items.length})
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {items.map((it, i) => (
          <div key={i} style={{ fontSize: 12, color: "#9fb0cc" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <CanalTag canal={it.canal} />
              <span>{it[titleField]}</span>
            </div>
            {subtitleField && it[subtitleField] ? (
              <p style={{ margin: "4px 0 0 46px", color: "#7c8db0" }}>
                {subtitleLabel ? `${subtitleLabel}: ` : ""}{it[subtitleField]}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

// Rola suavemente até o bloco pedido -- usado pelos atalhos da barra fixa
// de pendências.
function scrollToSection(id) {
  if (typeof document === "undefined") return;
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function StickySummaryBar({ itensSemAssumir, temasAbertos, perguntasAbertas }) {
  const total = itensSemAssumir + temasAbertos + perguntasAbertas;
  return (
    <div style={stickyBarStyle}>
      <span style={{ fontSize: 12.5, fontWeight: 800, color: total ? "#FCA5A5" : "#86EFAC" }}>
        {total ? `${total} pendência${total === 1 ? "" : "s"} precisando de alguém` : "Nenhuma pendência em aberto agora"}
      </span>
      {total ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {itensSemAssumir ? (
            <button type="button" style={jumpButtonStyle} onClick={() => scrollToSection("sinalizados-sem-assumir")}>
              Sinalizados ({itensSemAssumir})
            </button>
          ) : null}
          {temasAbertos ? (
            // Rotulo aqui NAO pode usar a palavra "urgente" -- ela ja e' o
            // badge de gravidade dos itens de risco real (SEVERITY_LABEL),
            // e esse atalho leva pra um card de leitura da IA, nao pra um
            // alerta formal. As duas coisas com o mesmo nome, lado a lado
            // na barra fixa, confundiam numa tela de triagem de crise
            // (achado da revisao de UX de 23/10/2026).
            <button type="button" style={jumpButtonStyle} onClick={() => scrollToSection("temas-urgentes")}>
              Sinais de atenção da IA ({temasAbertos})
            </button>
          ) : null}
          {perguntasAbertas ? (
            <button type="button" style={jumpButtonStyle} onClick={() => scrollToSection("perguntas-sem-resposta")}>
              Perguntas sem resposta ({perguntasAbertas})
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminAgentRelatorio() {
  const [status, setStatus] = useState("checking");
  const [viewerUser, setViewerUser] = useState(null);

  const today = new Date();
  // Default = "últimos 3 dias" (pedido do Rodrigo: o relatório é consultado
  // com frequência maior que uma vez por semana, entao 7 dias como abertura
  // trazia coisa velha demais pra primeira olhada).
  const threeDaysAgo = new Date(today.getTime() - 2 * 24 * 3600 * 1000);
  const [fromInput, setFromInput] = useState(fmtDateInput(threeDaysAgo));
  const [toInput, setToInput] = useState(fmtDateInput(today));
  // Qual atalho de período está valendo agora, só pra destacar o botão --
  // editar De/Até na mão (calendário) tira o destaque, já que nenhum
  // atalho corresponde mais exatamente ao intervalo escolhido.
  const [activePreset, setActivePreset] = useState("3dias");

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");
  // Qual alerta está sendo assumido agora (desabilita o botão clicado sem
  // travar os outros) -- pedido direto da revisao de UX: antes nao tinha
  // nenhum jeito de assumir um alerta a partir dessa tela.
  const [claimingId, setClaimingId] = useState(null);
  // Assumir vários itens do mesmo grupo (mesma pessoa) de uma vez só --
  // evita que duas pessoas da equipe assumam pedaços diferentes da mesma
  // situação sem saber (pedido do Rodrigo junto com o agrupamento acima).
  const [claimingGroupKey, setClaimingGroupKey] = useState(null);
  // Resolver/reabrir um item de temas urgentes / perguntas sem resposta --
  // mesmo padrao do claimingId acima, so' que a chave e' o itemKey (hash
  // estavel, ver insightItemKey no backend) em vez do id numerico do alerta.
  const [resolvingKey, setResolvingKey] = useState(null);
  // "Mostrar resolvidos" -- cada bloco (temas urgentes / perguntas sem
  // resposta) comeca escondendo quem ja foi resolvido, pra fila ficar so'
  // com o que ainda precisa de alguem (pedido do Rodrigo: distinguir "da'
  // pra agir" de "ja' foi cuidado").
  const [showResolvedTemas, setShowResolvedTemas] = useState(false);
  const [showResolvedPerguntas, setShowResolvedPerguntas] = useState(false);
  // Drill-down "o que o Barnabé respondeu" em cada item sinalizado -- pedido
  // do Rodrigo, 24/10/2026: "seria importante saber a resposta que o
  // barnabe deu... poderia até ser um drill down". contextByAlertId guarda
  // {loading, mensagens, error} por id de alerta, carregado sob demanda
  // (so' quando expande), nunca junto do relatório principal.
  const [expandedAlertIds, setExpandedAlertIds] = useState(() => new Set());
  const [contextByAlertId, setContextByAlertId] = useState({});

  async function toggleAlertContext(alertId) {
    setExpandedAlertIds((prev) => {
      const next = new Set(prev);
      if (next.has(alertId)) next.delete(alertId);
      else next.add(alertId);
      return next;
    });
    if (contextByAlertId[alertId]) return; // já carregado (ou carregando) antes
    setContextByAlertId((prev) => ({ ...prev, [alertId]: { loading: true, mensagens: [], error: "" } }));
    try {
      const data = await apiFetch(`/admin/agent/alertas/${alertId}/contexto`, { method: "GET", viewer: viewerUser });
      setContextByAlertId((prev) => ({ ...prev, [alertId]: { loading: false, mensagens: data?.mensagens || [], error: "" } }));
    } catch (error) {
      setContextByAlertId((prev) => ({ ...prev, [alertId]: { loading: false, mensagens: [], error: error?.message || "Não foi possível carregar a conversa." } }));
    }
  }

  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const canAccess = getEffectiveRoles(me).includes("ADMIN");
        if (canAccess && !me.passwordMustChange) {
          setViewerUser(me);
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, []);

  const canManage = status === "ready" && Boolean(viewerUser?.id);

  const loadReport = async (overrides) => {
    if (!canManage) return;
    const effectiveFrom = overrides?.from ?? fromInput;
    const effectiveTo = overrides?.to ?? toInput;
    setLoading(true);
    setFeedback("");
    try {
      const params = new URLSearchParams();
      if (effectiveFrom) params.set("from", new Date(`${effectiveFrom}T00:00:00`).toISOString());
      if (effectiveTo) params.set("to", new Date(`${effectiveTo}T23:59:59`).toISOString());
      const data = await apiFetch(`/admin/agent/relatorio?${params.toString()}`, { method: "GET", viewer: viewerUser });
      setReport(data);
    } catch (error) {
      setFeedback("Não foi possível gerar o relatório agora.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) loadReport();
  }, [canManage]);

  async function handleAssumirAlerta(alertId) {
    if (!canManage || claimingId) return;
    setClaimingId(alertId);
    setFeedback("");
    try {
      await apiPost(`/admin/agent/alertas/${alertId}/assumir`, { viewer: viewerUser });
      // Some da lista "sem assumir" na hora -- recarregar o relatório
      // inteiro só pra isso seria lento e perderia o filtro de período em
      // tela; aqui só remove o item localmente, já que o backend é quem
      // decidiu ("quem assumir primeiro vence").
      setReport((prev) =>
        prev
          ? {
              ...prev,
              alertas: {
                ...prev.alertas,
                abertosSemAssumir: prev.alertas.abertosSemAssumir.filter((x) => x.id !== alertId)
              }
            }
          : prev
      );
    } catch (error) {
      setFeedback(error?.message || "Não foi possível assumir esse alerta agora.");
    } finally {
      setClaimingId(null);
    }
  }

  // Assumir todos os itens de um grupo (mesma pessoa) de uma vez -- mesmo
  // endpoint do assumir individual, chamado em sequência. Se algum já tiver
  // sido assumido por outra pessoa no meio do caminho (ou qualquer outro
  // erro), recarrega o relatório inteiro em vez de arriscar mostrar um item
  // já assumido como se ainda estivesse livre.
  async function handleAssumirGrupo(alertIds) {
    if (!canManage || claimingId || claimingGroupKey) return;
    const groupKey = alertIds.join(",");
    setClaimingGroupKey(groupKey);
    setFeedback("");
    try {
      for (const alertId of alertIds) {
        await apiPost(`/admin/agent/alertas/${alertId}/assumir`, { viewer: viewerUser });
      }
      setReport((prev) =>
        prev
          ? {
              ...prev,
              alertas: {
                ...prev.alertas,
                abertosSemAssumir: prev.alertas.abertosSemAssumir.filter((x) => !alertIds.includes(x.id))
              }
            }
          : prev
      );
    } catch (error) {
      setFeedback(error?.message || "Não foi possível assumir todos os itens desse grupo agora. Atualizando a lista...");
      loadReport();
    } finally {
      setClaimingGroupKey(null);
    }
  }

  // Atualiza um item de temas urgentes / perguntas sem resposta nas duas
  // listas (so' uma tem o itemKey, mas custa nada checar as duas e manter
  // uma funcao so').
  function patchInsightItem(itemKey, patch) {
    setReport((prev) => {
      if (!prev?.insights) return prev;
      const aplicar = (lista) => (lista || []).map((it) => (it.itemKey === itemKey ? { ...it, ...patch } : it));
      return {
        ...prev,
        insights: {
          ...prev.insights,
          temasUrgentes: aplicar(prev.insights.temasUrgentes),
          perguntasSemResposta: aplicar(prev.insights.perguntasSemResposta)
        }
      };
    });
  }

  async function handleResolverItem(item, kind) {
    if (!canManage || resolvingKey) return;
    setResolvingKey(item.itemKey);
    setFeedback("");
    try {
      const r = await apiPost(`/admin/agent/insights/${item.itemKey}/resolver`, {
        viewer: viewerUser,
        body: { kind, personId: item.personId || null }
      });
      patchInsightItem(item.itemKey, { resolvido: true, resolvidoPor: r.resolvidoPor, resolvidoEm: r.resolvidoEm });
    } catch (error) {
      setFeedback(error?.message || "Não foi possível marcar como resolvido agora.");
    } finally {
      setResolvingKey(null);
    }
  }

  async function handleReabrirItem(item) {
    if (!canManage || resolvingKey) return;
    setResolvingKey(item.itemKey);
    setFeedback("");
    try {
      await apiPost(`/admin/agent/insights/${item.itemKey}/reabrir`, { viewer: viewerUser });
      patchInsightItem(item.itemKey, { resolvido: false, resolvidoPor: null, resolvidoEm: null });
    } catch (error) {
      setFeedback(error?.message || "Não foi possível reabrir esse item agora.");
    } finally {
      setResolvingKey(null);
    }
  }

  const applyDateRangePreset = (presetId) => {
    const now = new Date();
    let from = now;
    let to = now;
    if (presetId === "ontem") {
      const yesterday = new Date(now.getTime() - 86400000);
      from = yesterday;
      to = yesterday;
    } else if (presetId === "3dias") {
      from = new Date(now.getTime() - 2 * 86400000);
    } else if (presetId === "7dias") {
      from = new Date(now.getTime() - 6 * 86400000);
    } else if (presetId === "30dias") {
      from = new Date(now.getTime() - 29 * 86400000);
    } else if (presetId === "mes") {
      // Usa ano/mes calculados no fuso de Sao Paulo (nao getFullYear/getMonth
      // do navegador, que seguem o fuso de quem esta logado) -- mesmo motivo
      // do fmtDateInput acima.
      const [spYear, spMonth] = fmtDateInput(now).split("-");
      from = new Date(`${spYear}-${spMonth}-01T00:00:00`);
    }
    const fromStr = fmtDateInput(from);
    const toStr = fmtDateInput(to);
    setActivePreset(presetId);
    setFromInput(fromStr);
    setToInput(toStr);
    loadReport({ from: fromStr, to: toStr });
  };

  if (status === "checking") return <CenteredMessage>Carregando...</CenteredMessage>;
  if (status === "forbidden") return <CenteredMessage>Você não tem acesso a esta área.</CenteredMessage>;

  const insights = report?.insights;
  const alertas = report?.alertas;
  // Totais pra barra fixa de pendências -- só conta o que tem contato e
  // ainda não foi resolvido/assumido (mesmo critério usado nos blocos
  // abaixo), pra não misturar "dado de leitura" com "precisa de alguém".
  const itensSemAssumirCount = alertas?.abertosSemAssumir?.length || 0;
  const temasAbertosCount = insights ? groupInsightItems(insights.temasUrgentes).abertos.length : 0;
  const perguntasAbertasCount = insights ? groupInsightItems(insights.perguntasSemResposta).abertos.length : 0;

  return (
    <div style={pageStyle}>
      <Link to={ADMIN_PATH} style={backLinkStyle}>
        <ArrowLeft size={14} /> Voltar
      </Link>

      <h1 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 800 }}>Relatório de curadoria -- Agente Barnabé</h1>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: "#9fb0cc" }}>
        Visível só pra quem é admin. Mostra o que as pessoas mais perguntaram, o que o agente não conseguiu responder e o
        que precisa de atenção humana.
      </p>

      {report ? (
        <StickySummaryBar
          itensSemAssumir={itensSemAssumirCount}
          temasAbertos={temasAbertosCount}
          perguntasAbertas={perguntasAbertasCount}
        />
      ) : null}

      <div style={cardStyle}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {DATE_RANGE_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              style={presetButtonStyle(activePreset === preset.id)}
              onClick={() => applyDateRangePreset(preset.id)}
              disabled={loading}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#9fb0cc", marginBottom: 5 }}>De</label>
            <input type="date" value={fromInput} onChange={(e) => { setActivePreset(null); setFromInput(e.target.value); }} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#9fb0cc", marginBottom: 5 }}>Até</label>
            <input type="date" value={toInput} onChange={(e) => { setActivePreset(null); setToInput(e.target.value); }} style={inputStyle} />
          </div>
          <button type="button" style={buttonStyle} onClick={() => loadReport()} disabled={loading}>
            {loading ? "Gerando..." : "Gerar relatório"}
          </button>
        </div>
      </div>

      {feedback ? <p style={{ color: "#FCA5A5", fontSize: 13 }}>{feedback}</p> : null}

      {report ? (
        <>
          <div id="sinalizados-sem-assumir" style={cardStyle}>
            <h2 style={sectionTitleStyle}>
              <AlertTriangle size={16} color="#FCA5A5" aria-hidden="true" /> Itens sinalizados sem assumir ({alertas?.abertosSemAssumir?.length || 0})
            </h2>
            <p style={sectionHintStyle}>Pendências que o Barnabé sinalizou agora mesmo e que ninguém assumiu ainda -- independe do período escolhido acima.</p>
            {alertas?.abertosSemAssumir?.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {groupAlertasByPerson(alertas.abertosSemAssumir).map((grupo) => {
                  const multi = grupo.itens.length > 1;
                  const first = grupo.itens[0];
                  const ids = grupo.itens.map((x) => x.id);
                  const groupKey = ids.join(",");
                  const grupoUrgente = grupo.itens.some((x) => x.gravidade === "urgent");
                  // Gravidade mais alta entre os itens do grupo -- usada pra UMA barra
                  // colorida so', no card do grupo, em vez de repetir a cor em cada item
                  // (pedido do Rodrigo, 25/10/2026: "poe a barra por conversa").
                  const grupoGravidade = grupo.itens.reduce(
                    (max, x) => (SEVERITY_RANK[x.gravidade] > SEVERITY_RANK[max] ? x.gravidade : max),
                    "normal"
                  );
                  // Mesma pessoa = mesmo contato resolvido no backend (ver
                  // resolvedContato em agentReport.js) -- entao o link de
                  // WhatsApp/e-mail so precisa aparecer UMA vez no cabecalho
                  // do grupo, nao repetido em cada item (pedido do Rodrigo,
                  // 24/10/2026, vendo "Josi · WhatsApp" repetido 6x na
                  // mesma tela: ContatoLine renderizava em todo item mesmo
                  // dentro de um grupo, sem ser pego pelo ajuste anterior
                  // que so cobria o CanalTag/nome ao lado do badge).
                  const grupoContato = grupo.itens.find((x) => x.contato)?.contato || first.contato;
                  return (
                    <div
                      key={grupo.key}
                      style={
                        multi
                          ? {
                              // Uma barra so' por conversa, na cor da gravidade mais alta do
                              // grupo (grupoGravidade) -- antes cada item dentro do grupo tinha
                              // a sua propria barra/fundo coloridos, dava "Importante" e "Urgente"
                              // com o mesmo peso visual repetido varias vezes (pedido do Rodrigo,
                              // 25/10/2026: "mesma carga que exagero visual... poe a barra por
                              // conversa"). Os itens individuais ficam neutros -- ver AlertUnitCard.
                              border: "1px solid rgba(255,255,255,0.12)",
                              borderLeft:
                                grupoGravidade === "urgent"
                                  ? "4px solid #EF4444"
                                  : grupoGravidade === "high"
                                    ? "3px solid #FACC15"
                                    : "3px solid rgba(255,255,255,0.18)",
                              borderRadius: 12,
                              padding: "10px 12px 6px",
                              background: grupoGravidade === "urgent" ? "rgba(239,68,68,0.07)" : "rgba(255,255,255,0.035)",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.25)"
                            }
                          : undefined
                      }
                    >
                      {multi ? (
                        // Cabeçalho do grupo numa única linha (nome, canal, contagem, contato
                        // e as duas ações) -- antes eram 3 blocos empilhados (cabeçalho, depois
                        // o contato numa linha inteira própria, depois "Encaminhar tudo" em
                        // outra), repetindo "Josi" 2x e dando a "Encaminhar tudo" o mesmo peso
                        // visual de "Assumir tudo agora" (que é a ação que realmente importa
                        // aqui). Reformulação pedida pelo Rodrigo, 25/10/2026: "ainda esta ruim
                        // o layout, muito cheio de coisa".
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap", minWidth: 0, maxWidth: "100%", boxSizing: "border-box" }}>
                          <Users size={13} color="#9fb0cc" aria-hidden="true" />
                          <span style={{ fontSize: 12.5, fontWeight: 800, color: "#cbd5e1" }}>
                            {first.personName || first.contato?.nome || "Mesma pessoa"}
                          </span>
                          <CanalTag canal={first.canal} />
                          <span style={{ fontSize: 11, color: "#7c8db0" }}>{grupo.itens.length} itens -- mesma conversa</span>
                          <ContatoLine contato={grupoContato} urgent={grupoUrgente} compact />
                          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", minWidth: 0, maxWidth: "100%", boxSizing: "border-box" }}>
                            <ForwardButton
                              iconOnly
                              urgent={grupoUrgente}
                              label={`Encaminhar tudo (${grupo.itens.length})`}
                              buildContent={() => buildGrupoForwardContent(grupo, grupoContato)}
                            />
                            {/* Um botão só pro grupo inteiro -- quem assume já fala com a pessoa
                                sobre tudo, não tem por que assumir só um item dela de cada vez
                                (pedido do Rodrigo, 23/10/2026, vendo a lista agrupada em produção). */}
                            <button
                              type="button"
                              onClick={() => handleAssumirGrupo(ids)}
                              disabled={claimingGroupKey === groupKey || Boolean(claimingId)}
                              style={{
                                border: grupoUrgente ? "1px solid rgba(239,68,68,0.6)" : "1px solid rgba(134,239,172,0.4)",
                                background: grupoUrgente
                                  ? claimingGroupKey === groupKey
                                    ? "rgba(220,38,38,0.45)"
                                    : "#DC2626"
                                  : claimingGroupKey === groupKey
                                    ? "rgba(134,239,172,0.08)"
                                    : "rgba(134,239,172,0.14)",
                                color: grupoUrgente ? "#FFF5F5" : "#86EFAC",
                                borderRadius: 8,
                                padding: "5px 12px",
                                fontSize: 12,
                                fontWeight: 800,
                                cursor: claimingGroupKey === groupKey ? "default" : "pointer",
                                fontFamily: "inherit",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                                boxSizing: "border-box"
                              }}
                            >
                              {claimingGroupKey === groupKey
                                ? "Assumindo todos..."
                                : grupoUrgente
                                  ? `Assumir tudo agora (${grupo.itens.length})`
                                  : `Assumir todos (${grupo.itens.length})`}
                            </button>
                          </div>
                        </div>
                      ) : null}
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {buildRenderUnits(grupo.itens).map((unit) => {
                          const a = unit.kind === "chain" ? unit.items[unit.items.length - 1] : unit.item;
                          // Fora de um grupo, ou urgente dentro de um grupo: sempre aberto. O
                          // resto (Importante/Cuidado dentro de um grupo) entra colapsado --
                          // ver CollapsibleAlertUnit acima.
                          const Card = !multi || a.gravidade === "urgent" ? AlertUnitCard : CollapsibleAlertUnit;
                          return (
                            <Card
                              key={a.id}
                              unit={unit}
                              multi={multi}
                              expandedAlertIds={expandedAlertIds}
                              contextByAlertId={contextByAlertId}
                              onToggleContext={toggleAlertContext}
                              claimingId={claimingId}
                              claimingGroupKey={claimingGroupKey}
                              onAssumir={handleAssumirAlerta}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: "#9fb0cc" }}>Nenhum item em aberto no momento.</p>
            )}
          </div>

          {!insights && !report.insightsIndisponivel ? (
            <div style={cardStyle}>
              <p style={{ margin: 0, fontSize: 13, color: "#9fb0cc" }}>
                Leitura das conversas (principais perguntas, assuntos, perguntas sem resposta e temas urgentes) não está
                configurada neste ambiente.
              </p>
            </div>
          ) : null}

          {report.insightsIndisponivel ? (
            <div style={cardStyle}>
              <p style={{ margin: 0, fontSize: 13, color: "#FCA5A5" }}>{report.insightsIndisponivel}</p>
            </div>
          ) : null}

          {insights ? (
            <>
              <div id="temas-urgentes" style={cardStyle}>
                <h2 style={sectionTitleStyle}>
                  <Flame size={16} color={UI.brand} aria-hidden="true" /> Sinais de atenção da IA ({insights.temasUrgentes?.length || 0})
                </h2>
                <p style={sectionHintStyle}>Leitura da IA sobre as conversas do período -- sinais de urgência que apareceram no texto, mesmo sem ter virado alerta formal. Com contato = dá pra agir; sem contato = só estatística.</p>
                {insights.temasUrgentes?.length ? (() => {
                  const { abertos, resolvidos, semContato } = groupInsightItems(insights.temasUrgentes);
                  return (
                    <>
                      {abertos.length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {abertos.map((t) => (
                            <InsightItemCard
                              key={t.itemKey}
                              titulo={t.tema}
                              canal={t.canal}
                              contato={t.contato}
                              personId={t.personId}
                              resolvendo={resolvingKey === t.itemKey}
                              onResolver={() => handleResolverItem(t, "tema_urgente")}
                            >
                              <p style={{ margin: "0 0 4px", fontSize: 12, color: "#9fb0cc" }}>{t.porQue}</p>
                              {t.trecho ? <p style={{ margin: 0, fontSize: 12, fontStyle: "italic", color: "#cbd5e1" }}>"{t.trecho}"</p> : null}
                            </InsightItemCard>
                          ))}
                        </div>
                      ) : (
                        <p style={{ margin: 0, fontSize: 13, color: "#9fb0cc" }}>Nenhum tema com contato pendente no período.</p>
                      )}
                      <ResolvedToggle n={resolvidos.length} show={showResolvedTemas} onToggle={() => setShowResolvedTemas((v) => !v)} />
                      {showResolvedTemas && resolvidos.length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
                          {resolvidos.map((t) => (
                            <InsightItemCard
                              key={t.itemKey}
                              titulo={t.tema}
                              canal={t.canal}
                              contato={t.contato}
                              personId={t.personId}
                              resolvido
                              resolvidoPor={t.resolvidoPor}
                              resolvendo={resolvingKey === t.itemKey}
                              onReabrir={() => handleReabrirItem(t)}
                            >
                              <p style={{ margin: "0 0 4px", fontSize: 12, color: "#9fb0cc" }}>{t.porQue}</p>
                            </InsightItemCard>
                          ))}
                        </div>
                      ) : null}
                      <SemContatoList items={semContato} titleField="tema" />
                    </>
                  );
                })() : (
                  <p style={{ margin: 0, fontSize: 13, color: "#9fb0cc" }}>Nenhum tema urgente identificado no período.</p>
                )}
              </div>

              <div id="perguntas-sem-resposta" style={cardStyle}>
                <h2 style={sectionTitleStyle}>
                  <HelpCircle size={16} color="#FDE68A" aria-hidden="true" /> Perguntas sem resposta ({insights.perguntasSemResposta?.length || 0})
                </h2>
                <p style={sectionHintStyle}>Também da leitura da IA: lacuna de conteúdo, não urgência -- pergunta que o agente não soube responder bem. Com contato = dá pra agir; sem contato = só estatística.</p>
                {insights.perguntasSemResposta?.length ? (() => {
                  const { abertos, resolvidos, semContato } = groupInsightItems(insights.perguntasSemResposta);
                  return (
                    <>
                      {abertos.length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {abertos.map((p) => (
                            <InsightItemCard
                              key={p.itemKey}
                              titulo={p.pergunta}
                              canal={p.canal}
                              contato={p.contato}
                              personId={p.personId}
                              resolvendo={resolvingKey === p.itemKey}
                              onResolver={() => handleResolverItem(p, "pergunta_sem_resposta")}
                            >
                              <p style={{ margin: "0 0 2px", fontSize: 12, color: "#9fb0cc" }}>Agente respondeu: {p.oQueOAgenteRespondeu}</p>
                              {p.porQueFicouSemResposta ? (
                                <p style={{ margin: 0, fontSize: 12, color: "#cbd5e1" }}>{p.porQueFicouSemResposta}</p>
                              ) : null}
                            </InsightItemCard>
                          ))}
                        </div>
                      ) : (
                        <p style={{ margin: 0, fontSize: 13, color: "#9fb0cc" }}>Nenhuma pergunta com contato pendente no período.</p>
                      )}
                      <ResolvedToggle n={resolvidos.length} show={showResolvedPerguntas} onToggle={() => setShowResolvedPerguntas((v) => !v)} />
                      {showResolvedPerguntas && resolvidos.length ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
                          {resolvidos.map((p) => (
                            <InsightItemCard
                              key={p.itemKey}
                              titulo={p.pergunta}
                              canal={p.canal}
                              contato={p.contato}
                              personId={p.personId}
                              resolvido
                              resolvidoPor={p.resolvidoPor}
                              resolvendo={resolvingKey === p.itemKey}
                              onReabrir={() => handleReabrirItem(p)}
                            >
                              <p style={{ margin: "0 0 2px", fontSize: 12, color: "#9fb0cc" }}>Agente respondeu: {p.oQueOAgenteRespondeu}</p>
                            </InsightItemCard>
                          ))}
                        </div>
                      ) : null}
                      <SemContatoList items={semContato} titleField="pergunta" subtitleField="oQueOAgenteRespondeu" subtitleLabel="Agente respondeu" />
                    </>
                  );
                })() : (
                  <p style={{ margin: 0, fontSize: 13, color: "#9fb0cc" }}>Nenhuma pergunta sem resposta identificada.</p>
                )}
              </div>
            </>
          ) : null}

          <div style={groupDividerStyle}>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#7c8db0", textTransform: "uppercase", letterSpacing: 0.6 }}>
              Visão geral do período
            </span>
            <span style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
          </div>
          <p style={{ margin: "-8px 0 14px", fontSize: 12, color: "#7c8db0" }}>
            Estatística e leitura do período -- não é lista de pendência, nada aqui precisa de ação.
          </p>

          <div style={cardStyle}>
            <div style={{ display: "flex", gap: 24, justifyContent: "center", flexWrap: "wrap", marginBottom: 14 }}>
              <div style={statStyle}>
                <p style={{ ...statNumberStyle, fontSize: 20 }}>{report.volume.conversas}</p>
                <p style={statLabelStyle}>Conversas no período</p>
              </div>
              <div style={statStyle}>
                <p style={{ ...statNumberStyle, fontSize: 20 }}>{report.volume.mensagens}</p>
                <p style={statLabelStyle}>Mensagens trocadas</p>
              </div>
            </div>
            {report.volume.porCanal ? (
              <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: report.volume.truncado ? 14 : 0 }}>
                <CanalTag canal="site" />
                <span style={{ fontSize: 12, color: "#9fb0cc" }}>{pluralConversas(report.volume.porCanal.site || 0)}</span>
                <CanalTag canal="whatsapp" />
                <span style={{ fontSize: 12, color: "#9fb0cc" }}>{pluralConversas(report.volume.porCanal.whatsapp || 0)}</span>
              </div>
            ) : null}
            {report.volume.truncado ? (
              <p
                style={{
                  margin: "0 0 14px",
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: "#FDE68A",
                  background: "rgba(250,204,21,0.12)",
                  border: "1px solid rgba(250,204,21,0.35)",
                  borderRadius: 8,
                  padding: "8px 12px",
                  textAlign: "center"
                }}
              >
                Volume grande demais pro período escolhido -- a análise considerou só uma parte das conversas. Tente um
                período menor pra uma leitura mais completa.
              </p>
            ) : null}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 14 }}>
              <p style={{ margin: "0 0 12px", fontSize: 11, fontWeight: 800, color: "#7c8db0", textTransform: "uppercase", letterSpacing: 0.4 }}>
                Alertas do período (assumidos ou não)
              </p>
              {/* As duas colunas abaixo cortam os MESMOS alertas por dois
                  eixos diferentes (categoria vs. gravidade) -- sem rótulo
                  próprio, os números pareciam desalinhados/duplicados entre
                  si. Cada coluna agora tem seu subtítulo e há um divisor
                  vertical entre elas (achado da revisão de UX de
                  23/10/2026). */}
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <p style={{ margin: "0 0 8px", fontSize: 10.5, fontWeight: 800, color: "#5b6b8c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Por categoria
                  </p>
                  {Object.keys(alertas?.porCategoria || {}).length ? (
                    Object.entries(alertas.porCategoria).map(([cat, n]) => (
                      <div key={cat} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}>
                        <span style={{ color: "#cbd5e1" }}>{CATEGORY_LABEL[cat] || cat}</span>
                        <span style={{ fontWeight: 800 }}>{n}</span>
                      </div>
                    ))
                  ) : (
                    <p style={{ margin: 0, fontSize: 13, color: "#9fb0cc" }}>Sem alertas no período.</p>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 180, borderLeft: "1px dashed rgba(255,255,255,0.12)", paddingLeft: 24 }}>
                  <p style={{ margin: "0 0 8px", fontSize: 10.5, fontWeight: 800, color: "#5b6b8c", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Por gravidade
                  </p>
                  {Object.entries(alertas?.porGravidade || {}).map(([sev, n]) => (
                    <div key={sev} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, padding: "4px 0" }}>
                      <Badge tone={sev}>{SEVERITY_LABEL[sev] || sev}</Badge>
                      <span style={{ fontWeight: 800 }}>{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {insights ? (
            <>
              <div style={cardStyle}>
                <h2 style={sectionTitleStyle}>
                  <MessageCircleQuestion size={16} color={UI.brandBlue} aria-hidden="true" /> Principais assuntos
                </h2>
                {insights.principaisAssuntos?.length ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {insights.principaisAssuntos.map((a, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                        <span style={{ color: "#e5ebf7" }}>
                          <strong>{a.assunto}</strong> -- {a.resumo}
                        </span>
                        <span style={{ fontWeight: 800, whiteSpace: "nowrap" }}>{a.ocorrencias}x</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: 13, color: "#9fb0cc" }}>Sem dados suficientes no período.</p>
                )}
              </div>

              <div style={cardStyle}>
                <h2 style={sectionTitleStyle}>Principais perguntas</h2>
                {insights.principaisPerguntas?.length ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {insights.principaisPerguntas.map((p, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                        <span style={{ color: "#e5ebf7" }}>{p.pergunta}</span>
                        <span style={{ fontWeight: 800, whiteSpace: "nowrap" }}>{p.ocorrencias}x</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: 13, color: "#9fb0cc" }}>Sem dados suficientes no período.</p>
                )}
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
